import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { subscriptionApi } from '../services/api';
import { Subscription as SubscriptionType } from '../types';

const PLANS = [
  {
    id: 'FREE',
    name: 'Free',
    emoji: '🆓',
    description: 'Para empezar sin compromiso',
    monthlyPrice: 0,
    yearlyPrice: 0,
    badge: null,
    color: '#64748b',
    popular: false,
    features: [
      '1 negocio',
      '3 eventos al mes',
      '3 servicios',
      '3 empleados',
      'Perfil básico en el directorio',
      'Soporte por email',
    ],
    limits: ['Sin badge verificado', 'Sin posicionamiento destacado', 'Sin analytics'],
  },
  {
    id: 'STARTER',
    name: 'Starter',
    emoji: '⚡',
    description: 'Para negocios que arrancan',
    monthlyPrice: 9.9,
    yearlyPrice: 7.9,
    badge: 'verified',
    badgeLabel: '✓ Verificado',
    badgeColor: '#10b981',
    color: '#3b82f6',
    popular: false,
    features: [
      '1 negocio',
      '10 eventos al mes',
      '10 servicios',
      '5 empleados',
      'Badge ✓ Verificado',
      'Perfil con fotos',
      'Soporte prioritario',
    ],
    limits: ['Sin posicionamiento destacado', 'Analytics básico'],
  },
  {
    id: 'PRO',
    name: 'Pro',
    emoji: '🚀',
    description: 'Para negocios en crecimiento',
    monthlyPrice: 19.9,
    yearlyPrice: 15.9,
    badge: 'pro',
    badgeLabel: '⚡ Pro',
    badgeColor: '#3b82f6',
    color: '#6366f1',
    popular: true,
    features: [
      '1 negocio',
      'Eventos ilimitados',
      'Servicios ilimitados',
      '10 empleados',
      'Badge ⚡ Pro',
      'Aparecer primero en búsquedas',
      'Analytics avanzado',
      'Soporte prioritario',
    ],
    limits: [],
  },
  {
    id: 'BUSINESS',
    name: 'Business',
    emoji: '💎',
    description: 'Para equipos y cadenas',
    monthlyPrice: 39.9,
    yearlyPrice: 31.9,
    badge: 'premium',
    badgeLabel: '💎 Premium',
    badgeColor: '#f59e0b',
    color: '#f59e0b',
    popular: false,
    features: [
      'Negocios ilimitados',
      'Todo ilimitado',
      'Empleados ilimitados',
      'Badge 💎 Premium dorado',
      'TOP posición en directorio',
      'Analytics completo',
      'Soporte 24/7',
      'Acceso API',
    ],
    limits: [],
  },
];

export function Subscription() {
  const [currentSub, setCurrentSub] = useState<SubscriptionType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState('');

  useEffect(() => { fetchSub(); }, []);

  const fetchSub = async () => {
    try {
      const r = await subscriptionApi.getCurrentSubscription();
      setCurrentSub(r.data.data.subscription);
    } catch {} finally { setIsLoading(false); }
  };

  const handleSubscribe = async (planId: string) => {
    if (planId === 'FREE') return;
    setCheckoutLoading(planId);
    try {
      const r = await subscriptionApi.createCheckout({
        plan: planId,
        interval: isYearly ? 'yearly' : 'monthly',
        successUrl: `${window.location.origin}/subscription?success=true`,
        cancelUrl: `${window.location.origin}/subscription?canceled=true`,
      });
      window.location.href = r.data.data.checkoutUrl;
    } catch {
      toast.error('Error al iniciar el pago. Configura Stripe primero.');
      setCheckoutLoading('');
    }
  };

  const handleCancel = async () => {
    if (!confirm('¿Cancelar tu suscripción?')) return;
    try {
      await subscriptionApi.cancelSubscription();
      toast.success('Suscripción cancelada');
      fetchSub();
    } catch { toast.error('Error al cancelar'); }
  };

  const handleReactivate = async () => {
    try {
      await subscriptionApi.reactivateSubscription();
      toast.success('Suscripción reactivada');
      fetchSub();
    } catch { toast.error('Error al reactivar'); }
  };

  // Solo considerar plan activo si el status es ACTIVE o TRIALING
  const isActive = currentSub?.status === 'ACTIVE' || currentSub?.status === 'TRIALING';
  const currentPlan = (isActive ? currentSub?.plan : null) || 'FREE';

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-8">
      <style>{`
        .plan-card { background: white; border-radius: 20px; padding: 28px; border: 2px solid #e2e8f0; transition: all .3s; position: relative; }
        .plan-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,.1); }
        .plan-card.popular { border-color: #6366f1; background: linear-gradient(160deg, rgba(99,102,241,.04) 0%, white 60%); }
        .plan-card.current { border-color: #10b981; }
        .feat-item { display: flex; align-items: center; gap: 8px; font-size: .85rem; color: #475569; padding: 5px 0; }
        .feat-check { color: #10b981; flex-shrink: 0; font-size: .9rem; }
        .feat-x { color: #cbd5e1; flex-shrink: 0; font-size: .9rem; }
        .plan-btn { width: 100%; padding: 12px; border-radius: 10px; border: none; font-weight: 700; font-size: .9rem; cursor: pointer; transition: all .2s; margin-top: 20px; }
        .toggle-wrap { display: flex; align-items: center; gap: 12px; justify-content: center; margin-bottom: 40px; font-size: .9rem; color: #64748b; }
        .toggle { position: relative; width: 48px; height: 26px; background: #e2e8f0; border-radius: 100px; cursor: pointer; transition: background .3s; border: none; }
        .toggle.on { background: #6366f1; }
        .toggle-knob { position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; background: white; border-radius: 50%; transition: transform .3s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
        .toggle.on .toggle-knob { transform: translateX(22px); }
        .save-pill { background: #dcfce7; color: #15803d; font-size: .72rem; font-weight: 700; padding: 3px 10px; border-radius: 100px; }
      `}</style>

      {/* HEADER */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-secondary-900 mb-2">Planes y precios</h1>
        <p className="text-secondary-500 text-sm">Elige el plan que mejor se adapta a tu negocio. Sin permanencia.</p>
      </div>

      {/* SUSCRIPCIÓN ACTUAL */}
      {currentSub && (
        <div className="card p-6" style={{ borderLeft: '4px solid #10b981' }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-semibold text-secondary-400 uppercase mb-1">Plan actual</p>
              <p className="text-xl font-bold text-secondary-900">{currentSub.plan}</p>
              <p className="text-sm text-secondary-500 mt-1">
                Facturación {currentSub.interval === 'monthly' ? 'mensual' : 'anual'} ·{' '}
                <span className={
                  currentSub.status === 'ACTIVE' ? 'text-green-600 font-semibold' :
                  currentSub.status === 'TRIALING' ? 'text-blue-600 font-semibold' :
                  currentSub.status === 'UNPAID' ? 'text-red-600 font-semibold' :
                  'text-yellow-600 font-semibold'
                }>
                  {currentSub.status === 'ACTIVE' ? '✅ Activo' :
                   currentSub.status === 'TRIALING' ? '🔵 Prueba' :
                   currentSub.status === 'UNPAID' ? '❌ Pago pendiente' :
                   currentSub.status === 'PAST_DUE' ? '⚠️ Pago fallido' :
                   currentSub.status === 'CANCELED' ? '❌ Cancelado' :
                   currentSub.status}
                </span>
              </p>
              {currentSub.cancelAtPeriodEnd && (
                <p className="text-sm text-red-600 mt-1">
                  ⚠️ Se cancela el {new Date(currentSub.currentPeriodEnd).toLocaleDateString('es-ES')}
                </p>
              )}
            </div>
            <div>
              {currentSub.status === 'ACTIVE' && !currentSub.cancelAtPeriodEnd ? (
                <button onClick={handleCancel} style={{ padding: '9px 20px', borderRadius: 10, border: '1px solid #fee2e2', background: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '.85rem', cursor: 'pointer' }}>
                  Cancelar suscripción
                </button>
              ) : (
                <button onClick={handleReactivate} className="btn-primary">Reactivar</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TOGGLE MENSUAL/ANUAL */}
      <div className="toggle-wrap">
        <span style={{ fontWeight: !isYearly ? 700 : 400, color: !isYearly ? '#1e293b' : '#64748b' }}>Mensual</span>
        <button className={`toggle ${isYearly ? 'on' : ''}`} onClick={() => setIsYearly(!isYearly)}>
          <div className="toggle-knob"></div>
        </button>
        <span style={{ fontWeight: isYearly ? 700 : 400, color: isYearly ? '#1e293b' : '#64748b' }}>Anual</span>
        <span className="save-pill">Ahorra 20%</span>
      </div>

      {/* PLANES */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {PLANS.map(plan => {
          const isCurrent = currentPlan === plan.id;
          const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;

          return (
            <div key={plan.id} className={`plan-card ${plan.popular ? 'popular' : ''} ${isCurrent ? 'current' : ''}`}>

              {plan.popular && (
                <div style={{ position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', background: '#6366f1', color: 'white', fontSize: '.7rem', fontWeight: 700, padding: '4px 16px', borderRadius: '0 0 10px 10px', letterSpacing: '.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  ⭐ Más popular
                </div>
              )}
              {isCurrent && (
                <div style={{ position: 'absolute', top: -1, right: 16, background: '#10b981', color: 'white', fontSize: '.7rem', fontWeight: 700, padding: '4px 12px', borderRadius: '0 0 8px 8px' }}>
                  ✓ Tu plan
                </div>
              )}

              <div style={{ marginTop: plan.popular ? 12 : 0 }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>{plan.emoji}</div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>{plan.name}</h3>
                <p style={{ fontSize: '.78rem', color: '#64748b', marginBottom: 16 }}>{plan.description}</p>

                {plan.badge && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: plan.badge === 'premium' ? '#fef3c7' : plan.badge === 'pro' ? '#dbeafe' : '#dcfce7', color: plan.badgeColor, fontSize: '.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 100, marginBottom: 16, border: `1px solid ${plan.badge === 'premium' ? '#fcd34d' : plan.badge === 'pro' ? '#93c5fd' : '#86efac'}` }}>
                    {plan.badgeLabel}
                  </div>
                )}

                <div style={{ marginBottom: 20 }}>
                  <span style={{ fontSize: plan.monthlyPrice === 0 ? '1.8rem' : '2.2rem', fontWeight: 800, color: plan.color }}>
                    {plan.monthlyPrice === 0 ? 'Gratis' : `${price}€`}
                  </span>
                  {plan.monthlyPrice > 0 && <span style={{ fontSize: '.82rem', color: '#94a3b8' }}>/mes</span>}
                  {isYearly && plan.monthlyPrice > 0 && (
                    <p style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: 2 }}>antes {plan.monthlyPrice}€/mes</p>
                  )}
                </div>

                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16, marginBottom: 4 }}>
                  {plan.features.map((f, i) => (
                    <div key={i} className="feat-item"><span className="feat-check">✓</span>{f}</div>
                  ))}
                  {plan.limits.map((f, i) => (
                    <div key={i} className="feat-item" style={{ opacity: .5 }}><span className="feat-x">✕</span>{f}</div>
                  ))}
                </div>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isCurrent || checkoutLoading === plan.id}
                  className="plan-btn"
                  style={{
                    background: isCurrent ? '#f0fdf4' : plan.popular ? '#6366f1' : plan.monthlyPrice === 0 ? '#f1f5f9' : 'white',
                    color: isCurrent ? '#15803d' : plan.popular ? 'white' : plan.monthlyPrice === 0 ? '#64748b' : plan.color,
                    border: isCurrent ? '1px solid #86efac' : plan.popular ? 'none' : `2px solid ${plan.monthlyPrice === 0 ? '#e2e8f0' : plan.color}`,
                    cursor: isCurrent || plan.monthlyPrice === 0 ? 'default' : 'pointer',
                    boxShadow: plan.popular && !isCurrent ? '0 4px 16px rgba(99,102,241,.35)' : 'none',
                  }}
                >
                  {isCurrent ? '✓ Plan actual' :
                   checkoutLoading === plan.id ? 'Procesando...' :
                   plan.monthlyPrice === 0 ? 'Plan actual (gratis)' :
                   `Activar ${plan.name}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ textAlign: 'center', fontSize: '.78rem', color: '#94a3b8', marginTop: 8 }}>
        🔒 Pago seguro con Stripe · Sin permanencia · Cancela cuando quieras
      </p>
    </div>
  );
}
